import { tutorials } from '@/components/tutorial/tutorialSteps'
import { useCoreApi } from '@/composables/useApi'
import { useHandleError } from '@/composables/useHandleError'
import { useTutorialStore } from '@/store/tutorial'
import { IIntercomAction, TutorialEventAction } from '@lifk/types'

export function useIntercomActions() {
  const api = useCoreApi()
  const tutorial = useTutorialStore()
  const { logError } = useHandleError()

  // Must resolve to a concrete id: undefined would leave whichever tour ran last
  const tourIdFor = (action: IIntercomAction) =>
    tutorials.some((t) => t.id === action.ref) ? action.ref : tutorials[0].id

  const consume = async () => {
    if (!tutorial.isSupported) return
    const res = await api
      .get<{ actions: IIntercomAction[] }>('/users/intercom-actions')
      .catch((error) => {
        logError(error, 'Fetching queued Intercom actions failed')
        return null
      })
    const actions = res?.data?.actions ?? []
    const next = actions.find((a) => a.type === 'tour')
    if (!next) return
    tutorial.start(tourIdFor(next))
    await api
      .delete(`/users/intercom-actions/${next._id}`)
      .catch((error) =>
        logError(error, 'Resolving a queued Intercom action failed')
      )
  }

  const trackTutorial = async (
    action: TutorialEventAction,
    tutorialId: string
  ) => {
    await api
      .post('/users/intercom-actions/tutorial-events', { action, tutorialId })
      .catch((error) => logError(error, 'Reporting a tutorial event failed'))
  }

  return { consume, trackTutorial }
}
